import { keepPreviousData } from "@tanstack/react-query"
import { addMilliseconds } from "date-fns"
import { useMemo } from "react"

import { api } from "~/utils/api"
import type { PeriodAvailability, PeriodParts } from "~/utils/stockProjection"

/** colId -> key (variantId or skuId) -> that period's residual pair. */
export type AvailabilityByColumn = Map<string, Map<string, PeriodAvailability>>

export type PartsByColumn = Map<string, Map<string, PeriodParts>>

/** Column id for a projection date column. Producers and consumers must both derive ids through it. */
export const availabilityColumnId = (date: Date) => String(date.getTime())

export type ProjectionDateColumn = { id: string; firstDay: Date; lastDay: Date }

/**
 * Period starts for `getProjectedStockForPeriods`, which closes each period with the following
 * start. A lone column has none, so its own end is appended as a start; results are positional and
 * `buildAvailabilityColumns` drops the entry that has no column.
 */
export function projectionPeriodStarts(dates: ProjectionDateColumn[]): Date[] {
  const only = dates.length === 1 ? dates[0] : undefined
  return only ? [only.firstDay, addMilliseconds(only.lastDay, 1)] : dates.map((d) => d.firstDay)
}

type ProjectionOptions = {
  grain?: "variantId" | "skuId"
  /**
   * Adds per-key parts so hovers build from this request; only where something renders them.
   * Org-wide they're ~5x the pairs, so such grids fetch them per row from the open hover card.
   */
  includePeriodParts?: boolean
  /**
   * While the next period loads, keeps the months it shares with the current one on screen.
   * Never across a warehouse or grain change, whose numbers would be wrong.
   */
  keepPreviousWindow?: boolean
  enabled?: boolean
}

/**
 * Availability read off the closing-stock walk, so the percentage and the Closing Stock row
 * cannot disagree.
 *
 * `dates` are period starts, paired with the column id the grid keys on. SKU grain sums to
 * variant grain exactly, so a grid rendering both rows only needs the SKU request.
 */
export function useAvailabilityFromProjection(
  variantIds: string[],
  warehouseId: string | undefined,
  dates: ProjectionDateColumn[],
  {
    grain = "variantId",
    includePeriodParts = false,
    keepPreviousWindow = false,
    enabled = true,
  }: ProjectionOptions = {},
) {
  const periodStarts = useMemo(() => projectionPeriodStarts(dates), [dates])

  const { data, isLoading } = api.stockOutTimes.getProjectedStockForPeriods.useQuery(
    {
      variantIds,
      warehouseId,
      dates: periodStarts,
      perKey: true,
      perKeyPeriodParts: includePeriodParts,
      grain,
    },
    {
      enabled: enabled && variantIds.length > 0 && periodStarts.length > 0,
      // Without a window to guard, keep the previous page's data so a narrowing search doesn't
      // flip `isLoading` and unmount callers mid-search (SUP-425).
      placeholderData: keepPreviousWindow
        ? (previous, previousQuery) =>
            sameScope(previousQuery?.queryKey, warehouseId, grain) &&
            sharesAPeriod(previous, periodStarts)
              ? previous
              : undefined
        : keepPreviousData,
    },
  )

  return useMemo(
    () => ({ ...buildAvailabilityColumns(data, dates), isLoading }),
    [data, dates, isLoading],
  )
}

// Per-key values don't depend on the other keys asked for, so only warehouse and grain gate it.
function sameScope(
  queryKey: readonly unknown[] | undefined,
  warehouseId: string | undefined,
  grain: string,
): boolean {
  const input = (queryKey?.[1] as { input?: { warehouseId?: string; grain?: string } } | undefined)
    ?.input
  return (input?.warehouseId ?? "") === (warehouseId ?? "") && input?.grain === grain
}

function sharesAPeriod(previous: ProjectionResponse | undefined, periodStarts: Date[]): boolean {
  const starts = new Set(periodStarts.map((d) => d.getTime()))
  return previous?.periods.some((p) => starts.has(new Date(p.date).getTime())) ?? false
}

/** The response shape `buildAvailabilityColumns` reads. */
export type ProjectionResponse = {
  periods: { date: Date }[]
  perKey?: Record<string, PeriodAvailability[]> | undefined
  perKeyParts?: Record<string, PeriodParts[]> | undefined
  periodEndWeeks: number[]
  forecastWeeks: Record<string, number>
}

/** One build per response-and-columns pair; see `buildAvailabilityColumns`. */
const builtColumns = new WeakMap<object, WeakMap<object, AvailabilityColumns>>()

type AvailabilityColumns = ReturnType<typeof transposeToColumns>

/**
 * Transposes the response into the per-column maps the grid reads.
 *
 * Each key's series is positional: entry `i` belongs to the request's `i`th period start, which
 * `periods` restates. An entry whose start has no column is dropped rather than guessed at.
 *
 * Cached on the response and column identities: the maps are only ever read, so one build serves
 * every caller holding the same pair.
 */
export function buildAvailabilityColumns(
  data: ProjectionResponse | undefined,
  dates: { id: string; firstDay: Date }[],
): AvailabilityColumns {
  // No response yet still yields a full set of empty columns, keyed on the columns alone.
  const identity: object = data ?? dates
  let byDates = builtColumns.get(identity)
  if (byDates === undefined) {
    byDates = new WeakMap()
    builtColumns.set(identity, byDates)
  }

  const cached = byDates.get(dates)
  if (cached !== undefined) return cached

  const built = transposeToColumns(data, dates)
  byDates.set(dates, built)
  return built
}

function transposeToColumns(
  data: ProjectionResponse | undefined,
  dates: { id: string; firstDay: Date }[],
) {
  const byColumn: AvailabilityByColumn = new Map(dates.map((d) => [d.id, new Map()]))
  const partsByColumn: PartsByColumn = new Map(dates.map((d) => [d.id, new Map()]))

  // A kept-previous response belongs to the previous window, so place entries by period start.
  const columnIdByStart = new Map(dates.map((d) => [d.firstDay.getTime(), d.id]))
  const columnIds = (data?.periods ?? []).map((p) =>
    columnIdByStart.get(new Date(p.date).getTime()),
  )

  for (const [keyId, periods] of Object.entries(data?.perKey ?? {})) {
    periods.forEach((p, i) => {
      const colId = columnIds[i]
      if (colId !== undefined) byColumn.get(colId)?.set(keyId, p)
    })
  }
  if (data?.perKeyParts) {
    for (const [keyId, periods] of Object.entries(data.perKeyParts)) {
      periods.forEach((p, i) => {
        const colId = columnIds[i]
        if (colId !== undefined) partsByColumn.get(colId)?.set(keyId, p)
      })
    }
  }

  const endWeekByColumn = new Map<string, number>()
  columnIds.forEach((colId, i) => {
    const endWeek = data?.periodEndWeeks[i]
    if (colId !== undefined && endWeek !== undefined) endWeekByColumn.set(colId, endWeek)
  })

  // Keyed by column so consumers never redo the week math.
  const horizonByColumn = new Map<string, (keyIds: string[]) => boolean>()
  for (const d of dates) {
    const endWeek = endWeekByColumn.get(d.id)
    horizonByColumn.set(d.id, (keyIds) =>
      isBeyondForecastHorizon(endWeek, data?.forecastWeeks, keyIds),
    )
  }

  return { byColumn, partsByColumn, horizonByColumn }
}

/**
 * Whether a period end sits past the forecast horizon of every key behind it, which is what makes
 * a cell read "no forecast" rather than 0%. Keys with no known horizon abstain; if none is known
 * the answer is false rather than "beyond".
 *
 * `endWeek` is a period *end*, so `convertFromStartDatesToEndDays` puts it a millisecond shy of the
 * boundary — week 5 arrives as 4.99999999. Round up before indexing: the demand at that end reads
 * `forecasts[ceil(endWeek) - 1]`, so that index is what has to exist.
 */
export function isBeyondForecastHorizon(
  endWeek: number | undefined,
  forecastWeeks: Record<string, number> | undefined,
  keyIds: string[],
) {
  if (endWeek === undefined) return false
  let known = 0
  let covering = 0
  for (const id of keyIds) {
    const weeks = forecastWeeks?.[id]
    if (weeks === undefined) continue
    known++
    if (Math.ceil(endWeek) - 1 < weeks) covering++
  }
  return known > 0 && covering === 0
}

/** The period parts behind one grid cell, across the cell's keys. */
export function periodPartsForKeys(
  byKey: Map<string, PeriodParts> | undefined,
  keyIds: string[],
): PeriodParts[] {
  return keyIds.map((id) => byKey?.get(id)).filter((v): v is PeriodParts => v !== undefined)
}
